import { pr } from "aaa";
import { useOutsideAlerter } from "components/hooks/useOutsideAlerter";
import { ui } from "components/util/uiHelpers";
import { themeColors } from "muiTheme";
import * as React from "react";

const DropdownOption = ({
    id,
    onClick,
    onKeyDown,
    isSelected,
    children
}: {
    id: string,
    onClick: () => void,
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void,
    isSelected?: boolean,
    children: React.ReactNode
}) => (
    <div
        id={id}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="option"
        aria-selected={isSelected ? "true" : "false"}
        tabIndex={-1}
    >
        {children}
    </div>
);

export const AccessibleDropdown = <T,>({
    options = [],
    valueRenderer,
    optionRenderer,
    valueKey = "value",
    onChange,
    single,
    showDivider = true,
    openVariant = "bottom",
    dropDownStyle,
    style,
    defaultValues
}: {
    options?: T[],
    valueRenderer: (values: T[]) => React.ReactNode,
    optionRenderer: (option: T, isSelected: boolean) => React.ReactNode,
    valueKey: string,
    onChange?: (values: T[]) => void,
    single?: boolean,
    showDivider?: boolean,
    openVariant?: "top" | "bottom",
    dropDownStyle?: React.CSSProperties,
    style?: React.CSSProperties,
    defaultValues?: T[]
}) => {
    const [values, setValues] = React.useState<T[]>(defaultValues ?? []);
    const [isOpen, setIsOpen] = React.useState(false);

    const ref = React.useRef<HTMLDivElement | null>(null);
    const dropdownRef = React.useRef<HTMLDivElement | null>(null);
    const optionsRef = React.useRef<HTMLDivElement | null>(null);
    useOutsideAlerter(ref, () => setIsOpen(false));

    const handleClickDropdown = () => {
        setIsOpen(!isOpen);
    };

    React.useEffect(() => {
        if (isOpen && optionsRef?.current) {
            const firstElement = optionsRef.current.children.item(0);

            if (firstElement instanceof HTMLDivElement) {
                firstElement.focus();
            }
        }
    }, [isOpen]);

    const handleSelectOption = (option: T) => () => {
        const optionValue = option[valueKey];

        let newValues;

        if (single) {
            newValues = [option];
            setIsOpen(false);
        } else {
            newValues = !!values.find(value => value[valueKey] === optionValue)
                ? values.filter(value => value[valueKey] !== optionValue)
                : [...values ?? [], option];
        }

        setValues(newValues);

        if (onChange) {
            onChange(newValues);
        }
    };

    const handleKeyDownDropdown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
            setIsOpen(true);
        }
    };

    const handleKeyDownOption = (option: T) => (event: React.KeyboardEvent<HTMLDivElement>) => {
        const currentOptionIndex = options.findIndex(value => value[valueKey] === option[valueKey]);

        if (currentOptionIndex > -1) {
            if (event.key === "ArrowUp") {
                document.getElementById(options[currentOptionIndex - 1]?.[valueKey])?.focus();
            } else if (event.key === "ArrowDown") {
                document.getElementById(options[currentOptionIndex + 1]?.[valueKey])?.focus();
            } else if (event.key === "Enter" || event.key === " ") {
                handleSelectOption(option)();
            } else if (event.key === "Escape") {
                const drowdownElement = dropdownRef?.current;

                if (drowdownElement instanceof HTMLDivElement) {
                    drowdownElement.focus();
                }

                setIsOpen(false);
            } else if (event.key === "Tab") {
                setIsOpen(false);
            }
        }
    };

    return (
        <>
            {isOpen && (
                <div
                    // backdrop to prevent activating other elements on outside click
                    style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
                    onClick={(event: any) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                />
            )}
            <div ref={ref} style={{ position: "relative", ...style }}>
                <div
                    ref={dropdownRef}
                    onClick={handleClickDropdown}
                    onKeyDown={handleKeyDownDropdown}
                    role="listbox"
                    aria-expanded={isOpen ? "true" : "false"}
                    // prevent focus when there is a popup
                    tabIndex={ui.isPopupVisible ? -1 : 0}
                    style={{
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        height: 40,
                        padding: "8px 10px",
                        borderRadius: 4,
                        border: "1px solid #a3b5c9",
                        boxShadow: "inset 0px 2px 6px 0px rgba(0, 0, 0, 0.07)",
                        cursor: "pointer",
                        zIndex: isOpen ? 9001 : undefined
                    }}
                >
                    <pr.P_16_CONTENT style={{ flexGrow: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {valueRenderer(values)}
                    </pr.P_16_CONTENT>
                    <pr.IconChevronDown style={{ flexShrink: 0, color: ui.isDarkMode ? "white" : undefined }} />
                </div>
                {isOpen &&
                    <div
                        ref={optionsRef}
                        style={{
                            position: "absolute",
                            top: openVariant === "bottom" ? "calc(100% + 1px)" : undefined,
                            bottom: openVariant === "top" ? "calc(100% + 1px)" : undefined,
                            left: 0,
                            backgroundColor: "white",
                            boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.25)",
                            border: ui.isDarkMode ? `1px solid ${themeColors.d_grey_200}` : undefined,
                            borderRadius: 8,
                            width: "max-content",
                            cursor: "pointer",
                            ...dropDownStyle
                        }}
                    >
                        {options.map((option, index) => {
                            const optionValue = option[valueKey];
                            const isSelected = !!values.find(value => value[valueKey] === optionValue);

                            return (
                                <React.Fragment key={optionValue}>
                                    <DropdownOption
                                        id={optionValue}
                                        onClick={handleSelectOption(option)}
                                        onKeyDown={handleKeyDownOption(option)}
                                        isSelected={isSelected}
                                    >
                                        {optionRenderer(option, isSelected)}
                                    </DropdownOption>
                                    {showDivider && index !== options.length - 1 && <pr.LightSeparatorLine />}
                                </React.Fragment>
                            );
                        })}
                    </div>
                }
            </div>
        </>
    );
};



// WEBPACK FOOTER //
// ./src/components/common/AccessibleDropdown.tsx